import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getTheme } from "../http/themes";

const isValidMediaId = (id) =>
  typeof id === "string" && /^[0-9a-fA-F]{24}$/.test(id);

/** Normalize a theme's sticker library to the `{ mediaId, url }` shape forms use. */
export const themeStickersForForm = (theme, max) =>
  (theme?.stickers || [])
    .map((sticker) => ({
      mediaId: String(
        sticker?.mediaId?._id || sticker?.mediaId || sticker?._id || ""
      ),
      url: typeof sticker === "string" ? sticker : sticker?.url || "",
    }))
    .filter((sticker) => isValidMediaId(sticker.mediaId) && sticker.url)
    .slice(0, max);

/**
 * The theme a create wizard is working under, in the shape the forms use.
 * Built from the `?themeId=` deep link or from a theme picked in the form.
 */
export const themeToContext = (theme, maxStickers = 10) => ({
  themeId: String(theme._id),
  title: theme.title || "",
  startDate: theme.startDate || null,
  endDate: theme.endDate || null,
  stickers: themeStickersForForm(theme, maxStickers),
});

/**
 * Theme a create wizard was opened for (`?themeId=` in the URL, opened from
 * the theme form). Resolves to null when the param is absent or the theme
 * cannot be loaded; `loading` stays true until that is known so the form
 * does not mount with empty defaults first.
 */
export default function useThemeContext({ enabled = true, maxStickers = 10 } = {}) {
  const [searchParams] = useSearchParams();
  const themeId = enabled ? searchParams.get("themeId") || "" : "";
  const [state, setState] = useState({
    loading: Boolean(themeId),
    themeContext: null,
    error: null,
  });

  useEffect(() => {
    if (!themeId) {
      setState({ loading: false, themeContext: null, error: null });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await getTheme(themeId);
        const theme = response?.data;
        if (cancelled) return;
        if (!theme?._id) throw new Error("Theme not found");
        setState({
          loading: false,
          error: null,
          themeContext: themeToContext(theme, maxStickers),
        });
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setState({
            loading: false,
            themeContext: null,
            error:
              error?.response?.data?.message ||
              "Could not load the theme. Fill in the dates and stickers manually.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [themeId, maxStickers]);

  return state;
}
